import * as React from 'react';
import { Bell, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

import { useAuth } from '@/context/AuthContext';
import { useNotifications, type Notification as GeneralNotification } from '@/hooks/useNotifications';
import { useClientNotifications } from '@/hooks/useClientNotifications';
import { onOpenNotificationSheet } from '@/lib/notification-sheet-bus';
import { useOptimizedUserRole } from '@/hooks/useOptimizedUserRole';

// Derive a deep-link path for a notification when no explicit action_url is set.
// Always tries to send the user to the part of the platform the notification is about.
const deriveActionUrl = (
  n: { source: 'general' | 'client'; category: string; metadata?: any },
  isAttorneyContext: boolean,
): string => {
  const meta = n.metadata || {};
  const base = isAttorneyContext ? '/attorney-portal' : '/client-portal';
  const cat = (n.category || '').toLowerCase();

  // ID hints commonly stored in metadata across the platform
  const connectionId = meta.connection_id || meta.connectionId;
  const caseId = meta.case_id || meta.caseId;
  const invoiceId = meta.invoice_id || meta.invoiceId;
  const messageId = meta.message_id || meta.messageId;
  const documentId = meta.document_id || meta.documentId;
  const reviewId = meta.review_id || meta.request_id || meta.reviewId;
  const sponsorshipId = meta.sponsorship_id || meta.sponsorshipId;
  const clientId = meta.client_id || meta.clientId;

  switch (cat) {
    case 'connection':
      return connectionId
        ? `${base}?tab=connections&highlight=${connectionId}`
        : `${base}?tab=connections`;
    case 'message':
    case 'communication':
      if (connectionId) {
        return `${base}?tab=messages&connection=${connectionId}`;
      }
      // No connection_id in metadata — fall back to opening the most recent
      // unread conversation so the user lands on the actual message.
      return `${base}?tab=messages&auto=latest-unread`;
    case 'payment':
    case 'invoice':
    case 'billing':
      if (invoiceId) {
        return isAttorneyContext
          ? `${base}?tab=billing&invoice=${invoiceId}`
          : `${base}?tab=billing&invoice=${invoiceId}`;
      }
      return `${base}?tab=billing`;
    case 'sponsorship':
      return sponsorshipId
        ? `${base}?tab=billing&sponsorship=${sponsorshipId}`
        : `${base}?tab=billing`;
    case 'document':
      return documentId
        ? `${base}?tab=documents&document=${documentId}`
        : `${base}?tab=documents`;
    case 'case':
    case 'case_update':
      if (reviewId && isAttorneyContext) {
        return `${base}?tab=reviews&reviewId=${reviewId}`;
      }
      return caseId ? `${base}?tab=cases&case=${caseId}` : `${base}?tab=cases`;
    case 'deadline':
      return `${base}?tab=deadlines`;
    case 'appointment':
    case 'calendar':
      return `${base}?tab=calendar`;
    case 'escalation':
      return isAttorneyContext ? `${base}?tab=reviews` : `${base}`;
    case 'admin_alert':
    case 'system':
    default:
      return base;
  }
};


interface UnifiedNotification {
  id: string;
  source: 'general' | 'client';
  title: string;
  message: string;
  category: string;
  type: string;
  priority: string;
  is_read: boolean;
  created_at: string;
  action_url: string | null;
  metadata?: any;
}

const formatRelative = (iso: string) => {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return '';
  }
};

export const NotificationBellSheet: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isAttorney, isAdmin } = useOptimizedUserRole();
  const isAttorneyContext =
    isAttorney || isAdmin || (typeof window !== 'undefined' && window.location.pathname.startsWith('/attorney-portal'));
  const [open, setOpen] = React.useState(false);

  const general = useNotifications();
  const client = useClientNotifications();

  // Listen to global open events (toast clicks, deep links, etc.)
  React.useEffect(() => {
    return onOpenNotificationSheet(() => setOpen(true));
  }, []);

  // Detect the floating timer's actual width so the bell sits to its left
  // with the same gap the menu button keeps from the timer (16px on each side).
  const [timerLeftOffset, setTimerLeftOffset] = React.useState<number | null>(null);
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    let resizeObserver: ResizeObserver | null = null;

    const measure = () => {
      const el = document.querySelector('[data-timer-shortcut]') as HTMLElement | null;
      if (!el) {
        setTimerLeftOffset(null);
        if (resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver = null;
        }
        return;
      }
      const rect = el.getBoundingClientRect();
      // Offset from viewport right edge to the timer's LEFT edge.
      // The shared widget gap is applied separately below so spacing stays even.
      const offset = window.innerWidth - rect.left;
      setTimerLeftOffset(offset);
      if (!resizeObserver) {
        resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(el);
      }
    };

    measure();
    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', measure);
    return () => {
      mutationObserver.disconnect();
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const merged: UnifiedNotification[] = React.useMemo(() => {
    const a: UnifiedNotification[] = (general.notifications || []).map((n: GeneralNotification) => ({
      id: `g-${n.id}`,
      source: 'general',
      title: n.title,
      message: n.message,
      category: n.category,
      type: n.type,
      priority: n.priority,
      is_read: n.is_read,
      created_at: n.created_at,
      action_url: n.action_url || null,
      metadata: (n as any).metadata,
    }));
    const b: UnifiedNotification[] = (client.notifications || []).map((n) => ({
      id: `c-${n.id}`,
      source: 'client',
      title: n.title,
      message: n.message,
      category: n.category,
      type: n.type,
      priority: n.priority,
      is_read: n.is_read,
      created_at: n.created_at,
      action_url: n.action_url,
      metadata: n.metadata,
    }));
    return [...a, ...b].sort(
      (x, y) => new Date(y.created_at).getTime() - new Date(x.created_at).getTime(),
    );
  }, [general.notifications, client.notifications]);

  const unreadCount = merged.filter((n) => !n.is_read).length;

  // Don't render for signed-out users.
  if (!user) return null;

  const handleRowClick = (n: UnifiedNotification) => {
    const rawId = n.id.slice(2); // strip 'g-' or 'c-'
    if (!n.is_read) {
      if (n.source === 'general') general.markAsRead(rawId);
      else client.markAsRead(rawId);
    }
    setOpen(false);
    // Always deep-link. When the notification carries a review_id (lawyer
    // consultation / NDA review response), prefer the consultations
    // detail view over a generic messages link so the user lands directly
    // on the attorney's response.
    const meta = n.metadata || {};
    const reviewId = meta.review_id || meta.reviewId || meta.request_id;
    let target: string;
    if (reviewId) {
      target = isAttorneyContext
        ? `/attorney-portal?tab=reviews&reviewId=${reviewId}`
        : `/client-portal?tab=consultations&reviewId=${reviewId}`;
    } else if (n.action_url && n.action_url.startsWith('/')) {
      target = n.action_url;
    } else {
      target = deriveActionUrl(
        { source: n.source, category: n.category, metadata: n.metadata },
        isAttorneyContext,
      );
    }
    // Rewrite portal prefix when the stored action_url targets the wrong portal
    // (e.g. client_notifications may hardcode /client-portal but the user is an attorney).
    const corrected = isAttorneyContext
      ? target.replace(/^\/client-portal\b/, '/attorney-portal')
      : target.replace(/^\/attorney-portal\b/, '/client-portal');
    navigate(corrected);
  };

  const handleMarkAllRead = () => {
    general.markAllAsRead();
    client.markAllAsRead();
  };

  const handleClearAll = () => {
    if (merged.length === 0) return;
    const ok = typeof window !== 'undefined'
      ? window.confirm('Clear all notifications? This cannot be undone.')
      : true;
    if (!ok) return;
    general.clearAll();
    client.clearAll();
  };

  const handleDismiss = (
    e: React.MouseEvent,
    n: UnifiedNotification,
  ) => {
    e.stopPropagation();
    const rawId = n.id.slice(2);
    if (n.source === 'general') general.deleteNotification(rawId);
    else client.deleteNotification(rawId);
  };

  // Match the shared floating menu geometry so the bell keeps a tight,
  // consistent gap from the menu even when no timer widget is present.
  const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const isLaunchpad = typeof window !== 'undefined' && window.location.pathname.startsWith('/launchpad');
  const isDesktop = screenWidth >= 1024;
  const isTablet = screenWidth >= 640;
  const widgetGap = isDesktop ? 16 : isTablet ? 12 : 8;
  // On portals without the floating timer, keep the bell visually paired with
  // the menu button — especially on small screens where a larger gap reads as misaligned.
  const adjacentGap = isDesktop ? 8 : isTablet ? 4 : 2;
  const menuRightOffset = isDesktop ? 16 : isTablet ? 12 : 6;
  const menuSize = isDesktop ? 48 : isTablet ? 40 : 32;
  const bellRightOffset = menuRightOffset + menuSize + adjacentGap;
  const bellStyle: React.CSSProperties =
    timerLeftOffset !== null && !isLaunchpad
      ? { right: `${timerLeftOffset + widgetGap}px` }
      : { right: `${bellRightOffset}px` };

  return (
    <div className="fixed top-1.5 sm:top-3 lg:top-4 z-50 transition-[right]" style={bellStyle}>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="relative shadow-lg bg-background/80 backdrop-blur-sm h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 rounded-none border-foreground/30"
            aria-label="Open notifications"
          >
            <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
            {unreadCount > 0 && (
              <span
                className="absolute -bottom-1 -right-1 min-w-[16px] h-[16px] sm:min-w-[18px] sm:h-[18px] px-1 flex items-center justify-center bg-foreground text-background text-[9px] sm:text-[10px] font-bold tabular-nums border border-background"
                aria-label={`${unreadCount} unread notifications`}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Button>
        </SheetTrigger>

        <SheetContent
          side="right"
          className="w-full sm:max-w-md p-0 bg-background border-l border-foreground rounded-none flex flex-col"
        >
          {/* Editorial Header */}
          <div className="px-6 pt-8 pb-5 border-b border-foreground">
            <div className="flex items-start gap-3">
              <div className="h-[2px] w-8 bg-lexsavvy-yellow mt-3 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  Inbox
                </p>
                <div className="flex items-baseline justify-between gap-3 mt-1">
                  <h2 className="font-serif text-2xl tracking-tight leading-tight">
                    Notifications
                  </h2>
                  <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground tabular-nums">
                    {unreadCount > 0 ? `${unreadCount} unread` : 'All read'}
                  </span>
                </div>
              </div>
            </div>
            {merged.length > 0 && (
              <div className="mt-4 flex justify-end gap-5">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-[10px] uppercase tracking-[0.2em] font-semibold text-foreground/70 hover:text-foreground border-b border-transparent hover:border-lexsavvy-yellow transition-colors pb-0.5"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={handleClearAll}
                  className="text-[10px] uppercase tracking-[0.2em] font-semibold text-foreground/70 hover:text-destructive border-b border-transparent hover:border-destructive transition-colors pb-0.5"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* Notification List */}
          <div className="flex-1 overflow-y-auto">
            {merged.length === 0 ? (
              <div className="px-6 py-16 flex flex-col items-center text-center">
                <div className="h-[2px] w-8 bg-lexsavvy-yellow mb-5" />
                <Bell className="h-8 w-8 text-muted-foreground/40 mb-4" strokeWidth={1.25} />
                <p className="font-serif text-lg tracking-tight">All caught up</p>
                <p className="mt-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  No notifications
                </p>
              </div>
            ) : (
              <ul className="flex flex-col">
                {merged.map((n) => (
                  <li key={n.id} className="border-b border-border/60 last:border-b-0">
                    <div
                      className={cn(
                        'group relative flex transition-colors',
                        'hover:bg-muted/40',
                        !n.is_read && 'bg-muted/20',
                      )}
                    >
                      <button
                        onClick={() => handleRowClick(n)}
                        className="flex-1 min-w-0 text-left px-6 py-4 flex gap-3"
                      >
                        {/* Unread accent */}
                        <div
                          className={cn(
                            'w-[2px] flex-shrink-0 self-stretch',
                            !n.is_read ? 'bg-lexsavvy-yellow' : 'bg-transparent',
                          )}
                          aria-hidden
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <p
                              className={cn(
                                'text-sm leading-tight tracking-tight truncate',
                                !n.is_read ? 'font-semibold text-foreground' : 'text-foreground/70',
                              )}
                            >
                              {n.title}
                            </p>
                            <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground whitespace-nowrap flex-shrink-0">
                              {formatRelative(n.created_at)}
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                            {n.message}
                          </p>
                          {(n.priority === 'high' || n.priority === 'urgent') && (
                            <span className="inline-block mt-2 text-[9px] uppercase tracking-[0.2em] font-semibold border border-foreground px-1.5 py-0.5">
                              {n.priority}
                            </span>
                          )}
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => handleDismiss(e, n)}
                        aria-label="Dismiss notification"
                        className="self-start mt-3 mr-3 p-1 text-muted-foreground/60 hover:text-foreground opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-foreground px-6 py-4 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              {merged.length} total
            </span>
            <button
              onClick={() => {
                setOpen(false);
                navigate('/account?section=notifications#notification-preferences');
              }}
              className="text-[10px] uppercase tracking-[0.2em] font-semibold text-foreground/80 hover:text-foreground border-b border-transparent hover:border-lexsavvy-yellow transition-colors pb-0.5"
            >
              Preferences
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default NotificationBellSheet;
